import { Injectable } from '@angular/core';

import { catchError, concat, concatMap, exhaustMap, map, merge, of, switchMap, take, tap } from 'rxjs';

import { Actions, createEffect, ofType } from '@ngrx/effects';

import { V1_AuthService } from '@app/api/v1/auth/v1-auth.service';
import { PermissionService } from '@app/auth/services/permission.service';
import { V1AuthUser } from '@app/models/api/v1/auth/auth.model';
import { UserDashboardPreferenceService } from '@app/sloneek/services/user-dashboard-preference.service';
import { navigationActions } from '@app/store/actions/navigation.actions';

import { holdingActions, holdingCompaniesActions } from '../actions/holding.actions';
import { loginActions } from '../actions/login.actions';
import { userInfoActions } from '../actions/user-info.actions';

@Injectable()
export class HoldingEffects {
  public readonly changeCompany$ = createEffect(() =>
    this.actions$.pipe(
      ofType(holdingActions.start),
      exhaustMap(({ params }) =>
        this.v1_AuthService.holdingLogin(params).pipe(
          map(response => holdingActions.success({ response })),
          catchError(() => of(holdingActions.error())),
        ),
      ),
    ),
  );

  public readonly getHoldingCompanies$ = createEffect(() =>
    this.actions$.pipe(
      ofType(holdingCompaniesActions.start),
      exhaustMap(() =>
        this.v1_AuthService.getHoldingCompanies().pipe(
          map(response => holdingCompaniesActions.success({ response: response.data })),
          catchError(() => of(holdingCompaniesActions.error())),
        ),
      ),
    ),
  );

  public readonly handleHoldingSwitchSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(holdingActions.success),
      concatMap(({ response: { data } }) =>
        concat(
          of(loginActions.success({ response: data, skipNavigation: true })),
          of(navigationActions.reloadCurrentRoute()).pipe(
            tap(() => this.userDashboardPreferenceService.clearCompanyDashboardPreferenceCache()),
          ),
        ),
      ),
    ),
  );

  public readonly fetchHoldingCompaniesOnUserLoad$ = createEffect(() => {
    const userFromUserInfo$ = this.actions$.pipe(
      ofType(userInfoActions.success),
      map(a => a.response),
    );
    const userFromLogin$ = this.actions$.pipe(
      ofType(loginActions.success),
      map(a => a.response.user),
    );

    return merge(userFromUserInfo$, userFromLogin$).pipe(
      switchMap((user: V1AuthUser) =>
        this.permissionService.canAccessSettingsModule().pipe(
          take(1),
          map(canAccess => ({ user, canAccess })),
        ),
      ),
      switchMap(({ user, canAccess }) => {
        if (canAccess && user?.company?.is_within_holding) {
          return of(holdingCompaniesActions.start());
        }
        return of(holdingCompaniesActions.clear());
      }),
    );
  });

  constructor(
    private actions$: Actions,
    private v1_AuthService: V1_AuthService,
    private permissionService: PermissionService,
    private userDashboardPreferenceService: UserDashboardPreferenceService,
  ) {}
}
